import React, { useReducer } from 'react';
import { GetServerSideProps } from 'next';
import { RecruitmentSearchDocument, RecruitmentSearchInput } from 'src/client/generated';
import { initializeGraphqlClient } from 'src/client/graphql/client';
import ActionsFooter from 'src/client/components/Footer/ActionsFooter';
import HeadTag from 'src/client/components/common/HeadTag';
import getToken from 'src/auth0/getToken';
import { ContextState, RecruitmentListContext, createInitialState } from 'client/contexts/recruitments/RecruitmentList';
import { RecruitmentsUI } from 'client/components/recruitments';
import { recruitmentListReducer } from 'client/reducers/recruitents/RecruitmentList';
import { NormalizedCacheObject } from '@apollo/client';
import { format } from 'date-fns';
import { paths } from 'src/utils/paths';
import { seoTagValues } from 'src/utils/seo';
import { useSearchQueryParams } from 'client/hooks/recruitment/RecruitmentList/useSearchQueryParams';
import { ssrErrorHandler } from 'src/utils/ssrErrorHandler';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const session = await getToken(ctx, false);
  const token = !session ? null : session?.accessToken;
  const areaIds = ctx.query?.area_ids ? [ctx.query.area_ids].flat(1) : null;
  const licenseIds = ctx.query?.license_ids ? [ctx.query.license_ids].flat(1) : null;
  const jobBenefitIds = ctx.query?.job_benefit_ids ? [ctx.query.job_benefit_ids].flat(1) : null;
  const workingHourOptionIds = ctx.query?.working_hour_option_ids ? [ctx.query.working_hour_option_ids].flat(1) : null;
  const sortOptionId = ctx.query?.sort_option_id ? ctx.query.sort_option_id : null;
  const dateText = ctx.query.date ? (ctx.query.date as string) : format(new Date(), 'yyyy-MM-dd');

  //未ログイン時に「家から近い順」の並び替えをされた場合、そのクエリのみ排除してリダイレクトする
  if (!token && sortOptionId === 'proximity_to_home') {
    const redirectUrlQuery = Object.entries(ctx.query)
      .reduce<string[]>((accumulator, [key, value]) => {
        if (key !== 'sort_option_id') accumulator.push(`${key}=${value}`);
        return accumulator;
      }, [])
      .join('&');

    const redirectUrl = redirectUrlQuery.length
      ? `${paths.recruitments.index}?${redirectUrlQuery}`
      : paths.recruitments.index;

    return {
      redirect: {
        permanent: false,
        destination: redirectUrl,
      },
    };
  }

  const searchObj: RecruitmentSearchInput = { date: dateText };
  if (areaIds && areaIds.length > 0) {
    searchObj.areaIds = areaIds;
  }
  if (licenseIds && licenseIds.length > 0) {
    searchObj.licenseIds = licenseIds;
  }
  if (jobBenefitIds && jobBenefitIds.length > 0) {
    searchObj.jobBenefitIds = jobBenefitIds;
  }
  if (workingHourOptionIds && workingHourOptionIds.length > 0) {
    searchObj.workingHourOptionIds = workingHourOptionIds;
  }
  if (sortOptionId && typeof sortOptionId === 'string') {
    searchObj.sortOptionId = sortOptionId;
  }

  try {
    const graphqlClient = initializeGraphqlClient(null, token);
    await Promise.all([
      graphqlClient.query({
        query: RecruitmentSearchDocument,
        variables: {
          data: searchObj,
        },
      }),
    ]);

    return {
      props: {
        initialApolloState: graphqlClient.cache.extract(),
        areaIds,
        token,
      },
    };
  } catch (error) {
    return ssrErrorHandler({ error });
  }
};

type Props = {
  initialApolloState: NormalizedCacheObject;
  areaIds: string[] | null;
  token: string | null;
};

const Index = ({ areaIds, token }: Props) => {
  const currentDate = new Date();
  const queryInput = useSearchQueryParams(currentDate);
  const initialState = createInitialState(currentDate, areaIds);
  const initialStateForReducer: ContextState = {
    ...initialState,
    // URLのクエリパラメータから募集検索Queryの入力を復元している
    searchQueryParams: {
      ...queryInput,
    },
  };
  const [state, dispatch] = useReducer(recruitmentListReducer, initialStateForReducer);

  return (
    <>
      <HeadTag
        title={seoTagValues.common.title}
        description={seoTagValues.common.description}
        keywords={seoTagValues.common.description}
        canonical={`${process.env.BASE_URL}${paths.recruitments.index}`}
      />
      <RecruitmentListContext.Provider value={{ ...state, dispatch }}>
        <RecruitmentsUI token={token} />
      </RecruitmentListContext.Provider>
      <ActionsFooter token={token} />
    </>
  );
};

export default Index;
